import React, { useState, useMemo } from "react";
import { useIntl } from "react-intl";
import { ProgressSpinner } from "primereact/progressspinner";
import dayjs from "dayjs";
import List from "../../common/list";

const PointsHistory = ({ pointsHistory, requesting }) => {
  const intl = useIntl();
  const [pageQuery, setPageQuery] = useState({
    pageSize: 10,
    pageNo: 1,
    sortBy: "desc",
    sortColumn: "earnedAt",
  });

  // Local sorting and pagination
  const processedData = useMemo(() => {
    if (!pointsHistory || pointsHistory.length === 0) {
      return [];
    }

    let sortedData = [...pointsHistory];

    // Apply sorting
    if (pageQuery.sortColumn) {
      sortedData.sort((a, b) => {
        let aValue = a[pageQuery.sortColumn];
        let bValue = b[pageQuery.sortColumn];

        // Handle date sorting
        if (pageQuery.sortColumn === "earnedAt") {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }
        // Handle number sorting
        else if (pageQuery.sortColumn === "points") {
          aValue = parseInt(aValue) || 0;
          bValue = parseInt(bValue) || 0;
        }
        // Handle string sorting
        else if (typeof aValue === "string" && typeof bValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (pageQuery.sortBy === "asc") {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
    }

    // Apply pagination
    const startIndex = (pageQuery.pageNo - 1) * pageQuery.pageSize;
    const endIndex = startIndex + pageQuery.pageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [pointsHistory, pageQuery]);

  const handlePageChange = (event) => {
    setPageQuery((prev) => ({
      ...prev,
      pageNo: Math.floor(event.first / event.rows) + 1,
      pageSize: event.rows,
    }));
  };

  const handleOnSortChange = (event) => {
    setPageQuery((prev) => ({
      ...prev,
      sortColumn: event.sortField,
      sortBy: event.sortOrder === 1 ? "asc" : "desc",
      pageNo: 1, // Reset to first page when sorting
    }));
  };

  if (requesting) {
    return (
      <div className="flex justify-content-center p-4">
        <ProgressSpinner />
      </div>
    );
  }

  if (!pointsHistory || pointsHistory.length === 0) {
    return (
      <div className="text-center p-4">
        <i
          className="pi pi-clock"
          style={{ fontSize: "3rem", color: "#ccc" }}
        ></i>
        <h3>{intl.formatMessage({ id: "no_points_history" })}</h3>
      </div>
    );
  }

  if (requesting) {
    return (
      <div className="flex justify-content-center p-4">
        <ProgressSpinner />
      </div>
    );
  }

  if (!pointsHistory || pointsHistory.length === 0) {
    return (
      <div className="text-center p-4">
        <i
          className="pi pi-clock"
          style={{ fontSize: "3rem", color: "#ccc" }}
        ></i>
        <h3>{intl.formatMessage({ id: "no_points_history" })}</h3>
      </div>
    );
  }

  const dateBodyTemplate = (rowData) => {
    return dayjs(rowData.earnedAt).format("DD MMM, YYYY");
  };

  const pointsBodyTemplate = (rowData) => {
    return (
      <span className="font-semibold text-green-600">+{rowData.points}</span>
    );
  };

  const columns = [
    {
      name: "earnedAt",
      header: "awarded_on",
      sortable: true,
      body: dateBodyTemplate,
    },
    {
      name: "sourceName",
      header: "earned_from",
      sortable: true,
      body: (rowData) => rowData.sourceName || "-",
    },
    {
      name: "points",
      header: "points_awarded",
      sortable: true,
      body: pointsBodyTemplate,
    },
  ];

  return (
    <div className="points-history-container">
      <List
        data={processedData}
        columns={columns}
        loading={requesting}
        pagination={pointsHistory.length > pageQuery.pageSize}
        selectionMode={null}
        pageQuery={pageQuery}
        totalRecords={pointsHistory.length}
        handlePageChange={handlePageChange}
        handleOnSortChange={handleOnSortChange}
        dataKey="id"
      />
    </div>
  );
};

export default PointsHistory;
